import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { apiClient } from '../../services/api';
import { SurveyTemplateSummary, EmailGroupSummary, EmailGroupMember, SurveyResponseItem } from '../../types/survey';
import { useLanguage } from '../../contexts/LanguageContext';
import ExcelImportSurvey from './ExcelImportSurvey';

interface RespondentEntry {
  email: string;
  name: string;
}

interface UploadedQuestion {
  no: number;
  category: string;
  question: string;
  defaultAnswer?: string;
  customFields?: { [key: string]: string };
}

interface ExcelWorkbookData {
  fileName: string;
  sheetNames: string[];
  sheets: { [name: string]: Record<string, any>[] };
  columns: { [name: string]: string[] };
  fileData?: string; // base64 encoded file data
}

const SurveyCreate: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const templateFileInputRef = useRef<HTMLInputElement>(null);
  const [templates, setTemplates] = useState<SurveyTemplateSummary[]>([]);
  const [emailGroups, setEmailGroups] = useState<EmailGroupSummary[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [title, setTitle] = useState('');
  const [respondents, setRespondents] = useState<RespondentEntry[]>([{ email: '', name: '' }]);
  const [expiresAt, setExpiresAt] = useState('');
  const [creating, setCreating] = useState(false);
  const [bulkInput, setBulkInput] = useState('');
  const [inputMode, setInputMode] = useState<'individual' | 'bulk' | 'group' | 'csv' | 'excel'>('individual');
  const [aiEnabled, setAiEnabled] = useState(true);

  // Template file upload state
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadedQuestions, setUploadedQuestions] = useState<UploadedQuestion[]>([]);
  const [useUploadedFile, setUseUploadedFile] = useState(false);

  // Column mapping dialog state
  const [showColumnMapping, setShowColumnMapping] = useState(false);
  const [workbookData, setWorkbookData] = useState<ExcelWorkbookData | null>(null);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [questionColumn, setQuestionColumn] = useState('');
  const [categoryColumn, setCategoryColumn] = useState('');
  const [answerColumn, setAnswerColumn] = useState('');
  const [customColumns, setCustomColumns] = useState<string[]>([]);
  const [importedCustomColumns, setImportedCustomColumns] = useState<string[]>([]);
  const [sourceFileData, setSourceFileData] = useState<{ name: string; data: string } | null>(null);

  useEffect(() => {
    loadTemplates();
    loadEmailGroups();
    // Set default expiry to 30 days from now
    const d = new Date();
    d.setDate(d.getDate() + 30);
    setExpiresAt(d.toISOString().split('T')[0]);
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await apiClient.getSurveyTemplates();
      setTemplates(data);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  const loadEmailGroups = async () => {
    try {
      const data = await apiClient.getEmailGroups();
      setEmailGroups(data);
    } catch (err) {
      console.error('Failed to load email groups:', err);
    }
  };

  const addRespondent = () => {
    setRespondents(prev => [...prev, { email: '', name: '' }]);
  };

  const removeRespondent = (index: number) => {
    setRespondents(prev => prev.filter((_, i) => i !== index));
  };

  const updateRespondent = (index: number, field: 'email' | 'name', value: string) => {
    setRespondents(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const parseBulkInput = () => {
    const lines = bulkInput.trim().split('\n').filter(l => l.trim());
    const parsed: RespondentEntry[] = lines.map(line => {
      const parts = line.split(/[,\t]/).map(s => s.trim());
      return {
        email: parts[0] || '',
        name: parts[1] || parts[0] || ''
      };
    }).filter(r => r.email);

    if (parsed.length > 0) {
      setRespondents(prev => {
        const existing = prev.filter(r => r.email.trim());
        const newEntries = parsed.filter(p => !existing.some(e => e.email === p.email));
        return existing.length > 0 ? [...existing, ...newEntries] : parsed;
      });
      setInputMode('individual');
      setBulkInput('');
    }
  };

  const loadFromGroup = async (groupId: string) => {
    try {
      const group = await apiClient.getEmailGroup(groupId);
      if (group.members.length > 0) {
        setRespondents(prev => {
          const existing = prev.filter(r => r.email.trim());
          const newMembers = group.members.filter((m: EmailGroupMember) => !existing.some(e => e.email === m.email));
          return existing.length > 0 ? [...existing, ...newMembers] : group.members;
        });
        setInputMode('individual');
      }
    } catch (err) {
      console.error('Failed to load group:', err);
      alert(t('surveyCreate', 'groupLoadFailed'));
    }
  };

  const handleCsvFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text.trim().split('\n').filter(l => l.trim());

      // Skip header if it looks like one
      const startIndex = lines[0]?.match(/^(email|メール|名前|name)/i) ? 1 : 0;

      const parsed: RespondentEntry[] = lines.slice(startIndex).map(line => {
        const parts = line.split(/[,\t]/).map(s => s.trim());
        return {
          email: parts[0] || '',
          name: parts[1] || parts[0] || ''
        };
      }).filter(r => r.email && r.email.includes('@'));

      if (parsed.length > 0) {
        setRespondents(prev => {
          const existing = prev.filter(r => r.email.trim());
          const newEntries = parsed.filter(p => !existing.some(e => e.email === p.email));
          return existing.length > 0 ? [...existing, ...newEntries] : parsed;
        });
        setInputMode('individual');
      } else {
        alert(t('surveyCreate', 'noValidEmails'));
      }
    };
    reader.readAsText(file);
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const clearRespondents = () => {
    setRespondents([{ email: '', name: '' }]);
  };

  // Handle template file upload (CSV/Excel) - shows column mapping dialog
  const handleTemplateFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });

      // Convert file to base64 for storage
      const fileDataBase64 = btoa(
        new Uint8Array(data).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      // Parse all sheets
      const sheets: { [name: string]: Record<string, any>[] } = {};
      const columns: { [name: string]: string[] } = {};

      for (const sheetName of workbook.SheetNames) {
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '' });
        sheets[sheetName] = jsonData;
        if (jsonData.length > 0) {
          columns[sheetName] = Object.keys(jsonData[0]);
        } else {
          columns[sheetName] = [];
        }
      }

      // Store workbook data and show mapping dialog
      setWorkbookData({
        fileName: file.name,
        sheetNames: workbook.SheetNames,
        sheets,
        columns,
        fileData: fileDataBase64
      });

      // Auto-select first sheet with data
      const firstSheetWithData = workbook.SheetNames.find(name => sheets[name].length > 0) || workbook.SheetNames[0];
      setSelectedSheet(firstSheetWithData);

      // Auto-detect columns for initial selection
      const cols = columns[firstSheetWithData] || [];
      const questionPatterns = ['question', '質問', '問', 'q', 'item'];
      const categoryPatterns = ['category', 'カテゴリ', '分類', 'section'];
      const answerPatterns = ['answer', '回答', 'defaultAnswer', '回答例', 'デフォルト'];

      const autoQuestion = cols.find(c => questionPatterns.some(p => c.toLowerCase().includes(p))) || '';
      const autoCategory = cols.find(c => categoryPatterns.some(p => c.toLowerCase().includes(p))) || '';
      const autoAnswer = cols.find(c => answerPatterns.some(p => c.toLowerCase().includes(p))) || '';

      setQuestionColumn(autoQuestion);
      setCategoryColumn(autoCategory);
      setAnswerColumn(autoAnswer);

      setShowColumnMapping(true);
    } catch (err: any) {
      console.error('File parse error:', err);
      alert('ファイルの読み込みに失敗しました: ' + err.message);
    }

    // Reset input
    if (templateFileInputRef.current) templateFileInputRef.current.value = '';
  };

  // Handle sheet change in mapping dialog
  const handleSheetChange = (sheetName: string) => {
    setSelectedSheet(sheetName);

    // Auto-detect columns for the new sheet
    const cols = workbookData?.columns[sheetName] || [];
    const questionPatterns = ['question', '質問', '問', 'q', 'item'];
    const categoryPatterns = ['category', 'カテゴリ', '分類', 'section'];
    const answerPatterns = ['answer', '回答', 'defaultAnswer', '回答例', 'デフォルト'];

    const autoQuestion = cols.find(c => questionPatterns.some(p => c.toLowerCase().includes(p))) || '';
    const autoCategory = cols.find(c => categoryPatterns.some(p => c.toLowerCase().includes(p))) || '';
    const autoAnswer = cols.find(c => answerPatterns.some(p => c.toLowerCase().includes(p))) || '';

    setQuestionColumn(autoQuestion);
    setCategoryColumn(autoCategory);
    setAnswerColumn(autoAnswer);
    setCustomColumns([]); // Reset custom columns when sheet changes
  };

  // Toggle custom column selection
  const toggleCustomColumn = (col: string) => {
    setCustomColumns(prev =>
      prev.includes(col)
        ? prev.filter(c => c !== col)
        : [...prev, col]
    );
  };

  // Get available columns (excluding already selected ones)
  const getAvailableCustomColumns = () => {
    const cols = workbookData?.columns[selectedSheet] || [];
    const usedCols = [questionColumn, categoryColumn, answerColumn].filter(Boolean);
    return cols.filter(c => !usedCols.includes(c));
  };

  // Apply column mapping and extract questions
  const applyColumnMapping = () => {
    if (!workbookData || !selectedSheet || !questionColumn) {
      alert('質問カラムを選択してください');
      return;
    }

    const sheetData = workbookData.sheets[selectedSheet];

    const questions: UploadedQuestion[] = sheetData
      .filter(row => {
        const val = row[questionColumn];
        return val !== undefined && val !== null && String(val).trim() !== '';
      })
      .map((row, i) => {
        // Build custom fields object
        const customFields: { [key: string]: string } = {};
        customColumns.forEach(col => {
          customFields[col] = String(row[col] || '');
        });

        return {
          no: i + 1,
          category: categoryColumn ? String(row[categoryColumn] || '') : '',
          question: String(row[questionColumn] || ''),
          defaultAnswer: answerColumn ? String(row[answerColumn] || '') : '',
          customFields: customColumns.length > 0 ? customFields : undefined
        };
      });

    if (questions.length === 0) {
      alert('質問が見つかりませんでした');
      return;
    }

    setUploadedQuestions(questions);
    setImportedCustomColumns(customColumns);
    setUploadedFileName(workbookData.fileName);
    setUseUploadedFile(true);
    setSelectedTemplate('');
    setShowColumnMapping(false);

    // Store source file data for upload
    if (workbookData.fileData) {
      setSourceFileData({ name: workbookData.fileName, data: workbookData.fileData });
    }

    // Set default title from filename
    if (!title.trim()) {
      const baseName = workbookData.fileName.replace(/\.(xlsx?|csv)$/i, '');
      setTitle(baseName);
    }
  };

  // Cancel column mapping
  const cancelColumnMapping = () => {
    setShowColumnMapping(false);
    setWorkbookData(null);
    setSelectedSheet('');
    setQuestionColumn('');
    setCategoryColumn('');
    setAnswerColumn('');
    setCustomColumns([]);
  };

  const clearUploadedFile = () => {
    setUploadedQuestions([]);
    setUploadedFileName('');
    setUseUploadedFile(false);
    setImportedCustomColumns([]);
    setSourceFileData(null);
  };

  const handleCreate = async () => {
    const hasTemplate = selectedTemplate || (useUploadedFile && uploadedQuestions.length > 0);
    if (!hasTemplate || !title.trim()) {
      alert(t('surveyCreate', 'templateAndTitleRequired'));
      return;
    }

    const validRespondents = respondents.filter(r => r.email.trim());
    if (validRespondents.length === 0) {
      alert(t('surveyCreate', 'respondentRequired'));
      return;
    }

    try {
      setCreating(true);

      if (useUploadedFile && uploadedQuestions.length > 0) {
        // Create survey from uploaded file using import-with-responses endpoint
        // Pre-fill answers from Excel data
        const firstRespondent = validRespondents[0];
        const responses: SurveyResponseItem[] = uploadedQuestions.map(q => ({
          no: q.no,
          category: q.category,
          question: q.question,
          defaultAnswer: '',
          answer: q.defaultAnswer || '', // Pre-fill with imported answer
          notes: q.customFields ? Object.entries(q.customFields).map(([k, v]) => `[${k}]: ${v}`).join('\n') : '' // Store custom fields in notes
        }));

        const result = await apiClient.createSurveyWithPrefilledResponse({
          title: title.trim(),
          respondent: { email: firstRespondent.email.trim(), name: firstRespondent.name.trim() || firstRespondent.email.trim() },
          responses,
          expiresAt: expiresAt ? new Date(expiresAt + 'T23:59:59').toISOString() : undefined,
          aiEnabled,
          sourceFile: sourceFileData || undefined
        });

        navigate(`/admin/surveys/${result.survey.surveyId}`);
      } else {
        // Create survey from template
        const survey = await apiClient.createSurvey({
          templateId: selectedTemplate,
          title: title.trim(),
          respondents: validRespondents.map(r => ({ email: r.email.trim(), name: r.name.trim() || r.email.trim() })),
          expiresAt: expiresAt ? new Date(expiresAt + 'T23:59:59').toISOString() : undefined,
          aiEnabled
        });
        navigate(`/admin/surveys/${survey.surveyId}`);
      }
    } catch (err: any) {
      alert(t('surveyCreate', 'createFailed') + (err.response?.data?.error?.message || err.message));
    } finally {
      setCreating(false);
    }
  };

  const validCount = respondents.filter(r => r.email.trim()).length;

  return (
    <div style={{ padding: '30px', maxWidth: '800px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '24px' }}>{t('surveyCreate', 'title')}</h2>

      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {/* Show template/title/expiry only when NOT in Excel import mode */}
        {inputMode !== 'excel' && (
          <>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>{t('surveyCreate', 'template')}</label>

              {/* Template selection OR file upload */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                {/* Template dropdown */}
                <div style={{ flex: 1 }}>
                  {useUploadedFile ? (
                    <div style={{
                      padding: '10px 12px',
                      border: '1px solid #c6f6d5',
                      borderRadius: '4px',
                      backgroundColor: '#f0fff4',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <span style={{ color: '#38a169', fontWeight: 500 }}>{uploadedFileName}</span>
                        <span style={{ color: '#718096', fontSize: '0.85rem', marginLeft: '8px' }}>
                          ({uploadedQuestions.length}問)
                        </span>
                      </div>
                      <button
                        onClick={clearUploadedFile}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#fc8181',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                      >
                        クリア
                      </button>
                    </div>
                  ) : (
                    <select
                      value={selectedTemplate}
                      onChange={(e) => { setSelectedTemplate(e.target.value); setUseUploadedFile(false); }}
                      disabled={templates.length === 0}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '4px',
                        fontSize: '1rem',
                        backgroundColor: templates.length === 0 ? '#f7fafc' : 'white'
                      }}
                    >
                      <option value="">
                        {templates.length === 0 ? 'テンプレートなし（ファイルから作成）' : t('surveyCreate', 'selectTemplate')}
                      </option>
                      {templates.map(tmpl => (
                        <option key={tmpl.templateId} value={tmpl.templateId}>
                          {tmpl.title} ({tmpl.totalQuestions}{t('common', 'questions')})
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* OR separator */}
                <div style={{ color: '#a0aec0', fontSize: '0.85rem' }}>または</div>

                {/* File upload button */}
                <div>
                  <input
                    ref={templateFileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleTemplateFileUpload}
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={() => templateFileInputRef.current?.click()}
                    disabled={useUploadedFile}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: useUploadedFile ? '#a0aec0' : '#4299e1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: useUploadedFile ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    ファイルから
                  </button>
                </div>
              </div>

              {/* Show preview of uploaded questions */}
              {useUploadedFile && uploadedQuestions.length > 0 && (
                <div style={{ marginTop: '12px', maxHeight: '180px', overflowY: 'auto', overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.75rem' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                    <thead style={{ backgroundColor: '#f7fafc', position: 'sticky', top: 0 }}>
                      <tr>
                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', width: '35px' }}>No</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', maxWidth: '70px' }}>カテゴリ</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>質問</th>
                        {uploadedQuestions[0]?.defaultAnswer !== undefined && (
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f0fff4' }}>回答</th>
                        )}
                        {importedCustomColumns.map(col => (
                          <th key={col} style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', backgroundColor: '#ebf8ff', maxWidth: '80px' }}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {uploadedQuestions.map(q => (
                        <tr key={q.no}>
                          <td style={{ padding: '4px 8px', borderBottom: '1px solid #edf2f7' }}>{q.no}</td>
                          <td style={{ padding: '4px 8px', borderBottom: '1px solid #edf2f7', maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.category || '-'}</td>
                          <td style={{ padding: '4px 8px', borderBottom: '1px solid #edf2f7' }}>{q.question.substring(0, 35)}{q.question.length > 35 ? '...' : ''}</td>
                          {uploadedQuestions[0]?.defaultAnswer !== undefined && (
                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #edf2f7', backgroundColor: '#f0fff4', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.defaultAnswer || '-'}</td>
                          )}
                          {importedCustomColumns.map(col => (
                            <td key={col} style={{ padding: '4px 8px', borderBottom: '1px solid #edf2f7', backgroundColor: '#ebf8ff', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.customFields?.[col] || '-'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>{t('surveyCreate', 'surveyTitle')}</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('surveyCreate', 'titlePlaceholder')}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '1rem', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>{t('surveyCreate', 'deadline')}</label>
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '1rem' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={aiEnabled}
                  onChange={(e) => setAiEnabled(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>AIアシストを有効にする</span>
                <span style={{ color: '#718096', fontSize: '0.8rem' }}>（回答者がAIの提案を受けられます）</span>
              </label>
            </div>
          </>
        )}

        <div style={{ marginBottom: '20px' }}>
          {/* Hide respondent label/clear when in Excel mode */}
          {inputMode !== 'excel' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                {t('surveyCreate', 'respondents')} {validCount > 0 && <span style={{ color: '#718096', fontWeight: 400 }}>({validCount}{t('common', 'people')})</span>}
              </label>
              {validCount > 1 && (
                <button
                  onClick={clearRespondents}
                  style={{ padding: '4px 10px', backgroundColor: '#fff5f5', color: '#e53e3e', border: '1px solid #fed7d7', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}
                >
                  {t('surveyCreate', 'clear')}
                </button>
              )}
            </div>
          )}

          {/* Input mode tabs */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setInputMode('individual')}
              style={{
                padding: '6px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem',
                backgroundColor: inputMode === 'individual' ? '#2c5282' : '#edf2f7',
                color: inputMode === 'individual' ? 'white' : '#4a5568'
              }}
            >
              {t('surveyCreate', 'individual')}
            </button>
            <button
              onClick={() => setInputMode('bulk')}
              style={{
                padding: '6px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem',
                backgroundColor: inputMode === 'bulk' ? '#2c5282' : '#edf2f7',
                color: inputMode === 'bulk' ? 'white' : '#4a5568'
              }}
            >
              {t('surveyCreate', 'bulk')}
            </button>
            <button
              onClick={() => setInputMode('group')}
              style={{
                padding: '6px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem',
                backgroundColor: inputMode === 'group' ? '#2c5282' : '#edf2f7',
                color: inputMode === 'group' ? 'white' : '#4a5568'
              }}
            >
              {t('surveyCreate', 'fromGroup')}
            </button>
            <button
              onClick={() => setInputMode('csv')}
              style={{
                padding: '6px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem',
                backgroundColor: inputMode === 'csv' ? '#2c5282' : '#edf2f7',
                color: inputMode === 'csv' ? 'white' : '#4a5568'
              }}
            >
              {t('surveyCreate', 'csvImport')}
            </button>
            <button
              onClick={() => setInputMode('excel')}
              style={{
                padding: '6px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem',
                backgroundColor: inputMode === 'excel' ? '#2c5282' : '#edf2f7',
                color: inputMode === 'excel' ? 'white' : '#4a5568'
              }}
            >
              Excelインポート
            </button>
          </div>

          {/* Individual input */}
          {inputMode === 'individual' && (
            <div>
              {respondents.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="email"
                    value={r.email}
                    onChange={(e) => updateRespondent(i, 'email', e.target.value)}
                    placeholder={t('common', 'email')}
                    style={{ flex: 1, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.9rem' }}
                  />
                  <input
                    type="text"
                    value={r.name}
                    onChange={(e) => updateRespondent(i, 'name', e.target.value)}
                    placeholder={t('common', 'name')}
                    style={{ flex: 1, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.9rem' }}
                  />
                  {respondents.length > 1 && (
                    <button
                      onClick={() => removeRespondent(i)}
                      style={{ padding: '8px 12px', backgroundColor: '#fff5f5', color: '#e53e3e', border: '1px solid #fed7d7', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={addRespondent}
                style={{ padding: '6px 14px', backgroundColor: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', color: '#4a5568' }}
              >
                {t('surveyCreate', 'addRespondent')}
              </button>
            </div>
          )}

          {/* Bulk text input */}
          {inputMode === 'bulk' && (
            <div>
              <textarea
                value={bulkInput}
                onChange={(e) => setBulkInput(e.target.value)}
                placeholder={t('surveyCreate', 'bulkPlaceholder')}
                rows={6}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }}
              />
              <button
                onClick={parseBulkInput}
                style={{ marginTop: '8px', padding: '6px 14px', backgroundColor: '#2c5282', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
              >
                {t('common', 'add')}
              </button>
            </div>
          )}

          {/* Group selection */}
          {inputMode === 'group' && (
            <div>
              {emailGroups.length === 0 ? (
                <p style={{ color: '#718096', fontSize: '0.85rem' }}>
                  {t('surveyCreate', 'noGroups')}<a href="/admin/email-groups" style={{ color: '#2c5282' }}>{t('surveyCreate', 'groupManagementLink')}</a>{t('surveyCreate', 'noGroupsSuffix')}
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {emailGroups.map(g => (
                    <div key={g.groupId} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#f7fafc'
                    }}>
                      <div>
                        <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>{g.name}</span>
                        <span style={{ marginLeft: '8px', color: '#718096', fontSize: '0.8rem' }}>({g.memberCount}{t('common', 'people')})</span>
                        {g.description && <p style={{ margin: '2px 0 0', color: '#a0aec0', fontSize: '0.75rem' }}>{g.description}</p>}
                      </div>
                      <button
                        onClick={() => loadFromGroup(g.groupId)}
                        style={{ padding: '6px 12px', backgroundColor: '#2c5282', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}
                      >
                        {t('common', 'add')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CSV file upload */}
          {inputMode === 'csv' && (
            <div>
              <p style={{ color: '#718096', fontSize: '0.85rem', marginBottom: '10px' }}>
                {t('surveyCreate', 'csvDescription')}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                onChange={handleCsvFileUpload}
                style={{ fontSize: '0.9rem' }}
              />
            </div>
          )}

          {/* Excel import - full wizard */}
          {inputMode === 'excel' && (
            <div style={{ marginTop: '-10px' }}>
              <p style={{ color: '#718096', fontSize: '0.85rem', marginBottom: '16px' }}>
                Excelファイルから質問と回答をインポートして、事前入力済みのアンケートを作成します。
              </p>
              <ExcelImportSurvey
                onSuccess={(result) => {
                  navigate(`/admin/surveys/${result.survey.surveyId}`);
                }}
                onCancel={() => setInputMode('individual')}
              />
            </div>
          )}
        </div>

        {/* Hide bottom buttons when in Excel import mode (wizard has its own buttons) */}
        {inputMode !== 'excel' && (
          <div style={{ display: 'flex', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
            <button
              onClick={handleCreate}
              disabled={creating || (!selectedTemplate && !useUploadedFile) || !title.trim()}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2c5282',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: creating ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                opacity: creating || (!selectedTemplate && !useUploadedFile) || !title.trim() ? 0.6 : 1
              }}
            >
              {creating ? t('common', 'creating') : t('surveyCreate', 'createSurvey')}
            </button>
            <button
              onClick={() => navigate('/admin/surveys')}
              style={{ padding: '12px 24px', backgroundColor: '#edf2f7', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '1rem', color: '#4a5568' }}
            >
              {t('common', 'cancel')}
            </button>
          </div>
        )}
      </div>

      {/* Column Mapping Modal */}
      {showColumnMapping && workbookData && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '900px', width: '95%', maxHeight: '90vh', overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '1.2rem' }}>
              回答済みデータのインポート
            </h3>
            <p style={{ margin: '0 0 20px', color: '#718096', fontSize: '0.85rem' }}>
              {workbookData.fileName} - Excelから質問と回答済みデータをインポートします
            </p>

            {/* Sheet selection */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                1. シート選択
              </label>
              <select
                value={selectedSheet}
                onChange={(e) => handleSheetChange(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0',
                  borderRadius: '4px', fontSize: '1rem'
                }}
              >
                {workbookData.sheetNames.map(name => (
                  <option key={name} value={name}>
                    {name} ({workbookData.sheets[name]?.length || 0}行)
                  </option>
                ))}
              </select>
            </div>

            {/* Main column mappings */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                2. 基本カラムの設定
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', color: '#e53e3e' }}>
                    質問カラム *
                  </label>
                  <select
                    value={questionColumn}
                    onChange={(e) => setQuestionColumn(e.target.value)}
                    style={{
                      width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0',
                      borderRadius: '4px', fontSize: '0.9rem'
                    }}
                  >
                    <option value="">-- 選択 --</option>
                    {(workbookData.columns[selectedSheet] || []).map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', color: '#38a169' }}>
                    回答カラム（入力済み）
                  </label>
                  <select
                    value={answerColumn}
                    onChange={(e) => setAnswerColumn(e.target.value)}
                    style={{
                      width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0',
                      borderRadius: '4px', fontSize: '0.9rem'
                    }}
                  >
                    <option value="">-- なし --</option>
                    {(workbookData.columns[selectedSheet] || []).map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', color: '#718096' }}>
                    カテゴリカラム
                  </label>
                  <select
                    value={categoryColumn}
                    onChange={(e) => setCategoryColumn(e.target.value)}
                    style={{
                      width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0',
                      borderRadius: '4px', fontSize: '0.9rem'
                    }}
                  >
                    <option value="">-- なし --</option>
                    {(workbookData.columns[selectedSheet] || []).map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Custom columns selection */}
            {questionColumn && getAvailableCustomColumns().length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                  3. 追加カラムの選択（オプション）
                </label>
                <p style={{ margin: '0 0 8px', color: '#718096', fontSize: '0.8rem' }}>
                  インポートに含める追加のカラムを選択してください
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {getAvailableCustomColumns().map(col => (
                    <button
                      key={col}
                      onClick={() => toggleCustomColumn(col)}
                      style={{
                        padding: '6px 12px',
                        border: customColumns.includes(col) ? '2px solid #4299e1' : '1px solid #e2e8f0',
                        borderRadius: '20px',
                        backgroundColor: customColumns.includes(col) ? '#ebf8ff' : 'white',
                        color: customColumns.includes(col) ? '#2b6cb0' : '#4a5568',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: customColumns.includes(col) ? 600 : 400
                      }}
                    >
                      {customColumns.includes(col) ? '✓ ' : ''}{col}
                    </button>
                  ))}
                </div>
                {customColumns.length > 0 && (
                  <p style={{ margin: '8px 0 0', color: '#38a169', fontSize: '0.8rem' }}>
                    {customColumns.length}個のカラムを追加選択中
                  </p>
                )}
              </div>
            )}

            {/* Preview */}
            {questionColumn && workbookData.sheets[selectedSheet] && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '0.9rem' }}>
                  4. プレビュー ({workbookData.sheets[selectedSheet].filter(row => {
                    const val = row[questionColumn];
                    return val !== undefined && val !== null && String(val).trim() !== '';
                  }).length}件)
                </label>
                <div style={{
                  maxHeight: '400px', overflowY: 'auto', overflowX: 'auto', border: '1px solid #e2e8f0',
                  borderRadius: '4px', fontSize: '0.75rem'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                    <thead style={{ backgroundColor: '#f7fafc', position: 'sticky', top: 0 }}>
                      <tr>
                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', width: '35px' }}>No</th>
                        {categoryColumn && <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', maxWidth: '80px' }}>カテゴリ</th>}
                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>質問</th>
                        {answerColumn && <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f0fff4' }}>回答</th>}
                        {customColumns.map(col => (
                          <th key={col} style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', backgroundColor: '#ebf8ff', maxWidth: '100px' }}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {workbookData.sheets[selectedSheet]
                        .filter(row => {
                          const val = row[questionColumn];
                          return val !== undefined && val !== null && String(val).trim() !== '';
                        })
                        .map((row, i) => (
                          <tr key={i}>
                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #edf2f7' }}>{i + 1}</td>
                            {categoryColumn && <td style={{ padding: '4px 8px', borderBottom: '1px solid #edf2f7', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(row[categoryColumn] || '-')}</td>}
                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #edf2f7' }}>
                              {String(row[questionColumn] || '').substring(0, 40)}{String(row[questionColumn] || '').length > 40 ? '...' : ''}
                            </td>
                            {answerColumn && <td style={{ padding: '4px 8px', borderBottom: '1px solid #edf2f7', backgroundColor: '#f0fff4', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {String(row[answerColumn] || '')}
                            </td>}
                            {customColumns.map(col => (
                              <td key={col} style={{ padding: '4px 8px', borderBottom: '1px solid #edf2f7', backgroundColor: '#ebf8ff', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {String(row[col] || '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
              <button
                onClick={cancelColumnMapping}
                style={{
                  padding: '10px 20px', backgroundColor: '#edf2f7', border: 'none',
                  borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', color: '#4a5568'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={applyColumnMapping}
                disabled={!questionColumn}
                style={{
                  padding: '10px 20px', backgroundColor: questionColumn ? '#2c5282' : '#a0aec0',
                  color: 'white', border: 'none', borderRadius: '6px',
                  cursor: questionColumn ? 'pointer' : 'not-allowed', fontSize: '0.9rem'
                }}
              >
                適用
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyCreate;
